import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { propertyService } from 'services/api';
import { useAuth } from 'context/AuthContext';
import PropertyAdminModal from 'components/PropertyAdminModal';
import DeletePropertyModal from './DeletePropertyModal';
import PropertyDetailModal from './PropertyDetailModal';
import styles from './Properties.module.css';
import Paginacion from 'components/Paginacion';

export default function Properties() {
  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [alert, setAlert]         = useState(null);
  const [search, setSearch]       = useState('');
  const [page, setPage]           = useState(1);
  const [total, setTotal]         = useState(0);
  const [modalProp, setModalProp] = useState(null);
  const [deleteProp, setDeleteProp] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [delError, setDelError] = useState('');

  // Modal detalle (ver PropertyDetailModal.jsx)
  const [detailProp, setDetailProp] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoad, setDetailLoad] = useState(false);

  const LIMIT = 10;
  const irAPagina = (n) => { setPage(n); window.scrollTo({ top: 0 }); };
  const { isSuperAdmin } = useAuth();

  const load = (p, s) => {
    setLoading(true);
    propertyService.getAll({ page: p, limit: LIMIT, search: s })
      .then(r => {
        setItems(r.data.data || []);
        setTotal(r.data.total || 0);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(page, search); }, [page]); // eslint-disable-line

  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(1, search); }, 400);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  const confirmDelete = async () => {
    if (!deleteProp) return;
    setDeleting(true);
    setDelError('');
    try {
      await propertyService.delete(deleteProp.id);
      setAlert({ type: 'success', message: 'Propiedad eliminada' });
      setDeleteProp(null);
      load(page, search);
    } catch (err) {
      setDelError(err.response?.data?.message || 'No se pudo eliminar. Inténtalo de nuevo.');
    } finally {
      setDeleting(false);
    }
  };

  const openDetail = async (p) => {
    setDetailProp(p);
    setDetailData(null);
    setDetailLoad(true);
    try {
      const r = await propertyService.getOne(p.id);
      setDetailData(r.data);
    } catch { setDetailData(null); }
    finally { setDetailLoad(false); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1><i className={`fas fa-building ${styles.iconoCabecera}`} />Propiedades</h1>
          <p>Conjuntos y propiedades registradas</p>
        </div>
        <Link to="/admin/propiedades/create" className="btn btn-primary">
          <i className="fas fa-plus" /> Nueva propiedad
        </Link>
      </div>

      {alert && (
        <div className={`alert alert-${alert.type} ${styles.aviso}`} onClick={() => setAlert(null)}>
          {alert.message}
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <div className={styles.tituloLista}>
            <h2>Lista de propiedades</h2>
            <span className="badge badge-primary">{total}</span>
          </div>
          <div className={styles.buscadorCaja}>
            <i className={`fas fa-search ${styles.iconoBuscar}`} />
            <input
              type="text"
              className={`form-control ${styles.buscador}`}
              placeholder="Buscar por nombre, dirección o NIT..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>

        {loading ? <div className="loading"><div className="spinner" /></div> :
          items.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-building" />
              <p>{search ? `Sin resultados para "${search}"` : 'Sin propiedades registradas'}</p>
            </div>
          ) : (
            <>
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>ID</th><th>Nombre</th><th>NIT</th><th>Dirección</th><th>Tipo</th><th>Administrador</th><th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(p => (
                      <tr key={p.id}>
                        <td>#{p.id}</td>
                        <td><strong>{p.name}</strong></td>
                        <td>{p.nit || '—'}</td>
                        <td>{p.address}</td>
                        <td>{p.tipo_propiedad ? p.tipo_propiedad.charAt(0).toUpperCase() + p.tipo_propiedad.slice(1) : '—'}</td>
                        <td>{p.admins?.[0]?.user ? `${p.admins[0].user.name} ${p.admins[0].user.last_name || ''}`.trim() : '—'}</td>
                        <td>
                          <div className="actions">
                            {isSuperAdmin && (
                              <button className="btn btn-sm btn-primary" onClick={() => setModalProp(p)} title="Asignar administrador">
                                <i className="fas fa-user-tie" /> Asignar admin
                              </button>
                            )}
                            <button className="btn btn-sm btn-secondary btn-icon" title="Ver propiedad" onClick={() => openDetail(p)}>
                              <i className="fas fa-eye" />
                            </button>
                            <Link to={`/admin/propiedades/${p.id}/edit`} className="btn btn-sm btn-secondary btn-icon" title="Editar propiedad">
                              <i className="fas fa-pencil" />
                            </Link>
                            <button className="btn btn-sm btn-danger btn-icon" onClick={() => setDeleteProp(p)} title="Eliminar propiedad">
                              <i className="fas fa-trash" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

            </>
          )}
      </div>
      {!loading && <Paginacion pagina={page} total={total} porPagina={LIMIT} onCambiar={irAPagina} />}

      {/* Modal asignar admin */}
      {modalProp && (
        <PropertyAdminModal
          fixedProperty={modalProp}
          onClose={() => setModalProp(null)}
          onSuccess={msg => {
            setAlert({ type: 'success', message: msg });
            setModalProp(null);
            load(page, search);
          }}
        />
      )}

      {/* Modal confirmar eliminar */}
      {deleteProp && (
        <DeletePropertyModal
          property={deleteProp}
          deleting={deleting}
          error={delError}
          onClose={() => { if (!deleting) { setDeleteProp(null); setDelError(''); } }}
          onConfirm={confirmDelete}
        />
      )}

      {/* Modal detalle propiedad */}
      {detailProp && (
        <PropertyDetailModal
          property={detailProp}
          data={detailData}
          loading={detailLoad}
          onClose={() => setDetailProp(null)}
        />
      )}
    </div>
  );
}